// providers/GeneralDataProvider.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GeneralDataContext, type WebsiteGeneralData } from './GeneralDataContext';
import { route } from '../utils/apiRoutes';

const GeneralDataProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [data, setData] = useState<WebsiteGeneralData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGeneral = useCallback(async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      // WordPress API returns { acf: {...} } structure
      const res = await fetch(route.generalData());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = await res.json();
      // API returns { acf: {...} }, extract the acf object
      const acf = payload?.acf || payload || {};
      setData(acf as WebsiteGeneralData);
    } catch (e: any) {
      console.error('Error fetching general data:', e);
      setError(e?.message || 'Failed to load general data');
      setData(null);
      setInitialLoading(false); // Make sure to set initialLoading to false even on error
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchGeneral(); }, [fetchGeneral]);

  const value = useMemo(() => ({
    data,
    loading,
    initialLoading,
    error,
    refresh: fetchGeneral,
  }), [data, loading, initialLoading, error, fetchGeneral]);

  return <GeneralDataContext.Provider value={value}>{children}</GeneralDataContext.Provider>;
};

export default GeneralDataProvider;
export { GeneralDataProvider };
